import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  ModalDialog,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { MdOutlineChat } from 'react-icons/md';
import { IconWrapper } from '../../components/IconWrapper';
import { WorkspaceCardHeader } from '../../components/WorkspaceCardHeader';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import type { Project } from '../../types/project';
import {
  commentRoleLabel,
  commentWasEdited,
  isOwnComment,
  type ConversationComment,
} from './workspaceComments.model';
import {
  addWorkspaceComment,
  deleteWorkspaceComment,
  loadWorkspaceConversation,
  promoteLegacyWorkspaceNote,
  updateWorkspaceComment,
  WORKSPACE_COMMENTS_MISSING,
} from './workspaceComments.service';

function formatStamp(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/London',
  });
}

const WorkspaceCommentsCard: React.FC<{
  project: Project;
  onProjectPatch: (patch: Partial<Project>) => Promise<void>;
}> = ({ project, onProjectPatch }) => {
  const { user } = useSupabaseAuth();
  const [comments, setComments] = useState<ConversationComment[]>([]);
  const [tableMissing, setTableMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ConversationComment | null>(null);
  const [deleting, setDeleting] = useState(false);

  const reload = useCallback(async () => {
    const kept = await promoteLegacyWorkspaceNote({
      id: project.id,
      notes: project.notes,
      created_at: project.created_at,
    });
    if (kept && String(project.notes || '').trim()) {
      await onProjectPatch({ notes: null });
    }
    const next = await loadWorkspaceConversation(project.id);
    setComments(next.comments);
    setTableMissing(next.tableMissing);
  }, [onProjectPatch, project.created_at, project.id, project.notes]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    reload()
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load comments'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reload]);

  const add = async () => {
    if (!user?.id || saving || !draft.trim() || tableMissing) return;
    setSaving(true);
    setError(null);
    try {
      await addWorkspaceComment(project.id, user.id, draft);
      setDraft('');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editingId || savingEdit) return;
    setSavingEdit(true);
    setError(null);
    try {
      await updateWorkspaceComment(editingId, editDraft);
      setEditingId(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update comment');
    } finally {
      setSavingEdit(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteWorkspaceComment(deleteTarget.id);
      setDeleteTarget(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete comment');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <WorkspaceCardHeader
          icon={MdOutlineChat}
          title="Comments"
        />
        {tableMissing && <Alert color="warning" sx={{ mb: 1 }}>{WORKSPACE_COMMENTS_MISSING}</Alert>}
        {error && <Alert color="danger" sx={{ mb: 1 }}>{error}</Alert>}
        {loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size="sm" /></Stack>
        ) : (
          <Stack gap={1.25}>
            {comments.length === 0 && (
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>No comments yet.</Typography>
            )}
            {comments.map((comment) => {
              const mine = isOwnComment(comment, user?.id);
              const editing = editingId === comment.id;
              const canManage = mine && !tableMissing;
              const roleLabel = commentRoleLabel(comment.authorRole, comment.authorName);
              return (
                <Card
                  key={comment.id}
                  variant="soft"
                  color={mine ? 'primary' : 'neutral'}
                  sx={{ width: '100%', boxShadow: 'none' }}
                >
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                      <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                        <Typography level="title-sm">{comment.authorName}</Typography>
                        {roleLabel && <Chip size="sm" variant="outlined">{roleLabel}</Chip>}
                        {mine && <Chip size="sm" variant="solid" color="primary">You</Chip>}
                      </Stack>
                      {canManage && !editing && (
                        <Stack direction="row" gap={0.25}>
                          <IconButton
                            size="sm"
                            variant="plain"
                            aria-label="Edit comment"
                            onClick={() => { setEditingId(comment.id); setEditDraft(comment.body); }}
                          >
                            <IconWrapper icon={FaEdit} />
                          </IconButton>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            aria-label="Delete comment"
                            onClick={() => setDeleteTarget(comment)}
                          >
                            <IconWrapper icon={FaTrash} />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>
                    <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 0.5 }}>
                      {formatStamp(comment.createdAt)}
                      {commentWasEdited(comment) ? ' · Edited' : ''}
                    </Typography>
                    {editing ? (
                      <Stack gap={0.75}>
                        <Textarea
                          minRows={2}
                          value={editDraft}
                          onChange={(event) => setEditDraft(event.target.value)}
                        />
                        <Stack direction="row" justifyContent="flex-end" gap={1}>
                          <Button size="sm" variant="plain" disabled={savingEdit} onClick={() => setEditingId(null)}>
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            loading={savingEdit}
                            disabled={!editDraft.trim()}
                            onClick={() => { void saveEdit(); }}
                          >
                            Save
                          </Button>
                        </Stack>
                      </Stack>
                    ) : (
                      <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                        {comment.body}
                      </Typography>
                    )}
                </CardContent>
                </Card>
              );
            })}
            <Textarea
              minRows={2}
              placeholder="Add a comment on this workspace"
              value={draft}
              disabled={tableMissing}
              onChange={(event) => setDraft(event.target.value)}
            />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                size="md"
                disabled={tableMissing || !draft.trim()}
                loading={saving}
                onClick={() => { void add(); }}
                sx={{ minHeight: 44 }}
              >
                Add comment
              </Button>
            </Stack>
          </Stack>
        )}
      </CardContent>

      <Modal open={!!deleteTarget} onClose={() => { if (!deleting) setDeleteTarget(null); }}>
        <ModalDialog>
          <DialogTitle>Delete comment</DialogTitle>
          <DialogContent>
            <Typography level="body-sm">This removes the comment from the workspace conversation.</Typography>
          </DialogContent>
          <Stack direction="row" justifyContent="flex-end" gap={1}>
            <Button variant="plain" disabled={deleting} onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="danger" loading={deleting} onClick={() => { void remove(); }}>Delete</Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Card>
  );
};

export default WorkspaceCommentsCard;
